import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";

import React from "react";

import { GLASS_PANEL_CLASS } from "@replohq/lattice/blocks/get-started-card/glassSurface";
import {
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
} from "@replohq/lattice/components/collapsible/Collapsible";
import { cn } from "@replohq/lattice/utils/cn";
import { Check, ChevronDown } from "lucide-react";

/** The one step shape every card uses, so a merchant learns it once. */
export function WorkflowStep<ClassName extends string = string>({
  chatAction,
  status,
  repeatLabel,
  className,
  UNSAFE_className,
  result,
  label,
  description,
  optional = false,
  stepNumber,
  open,
  onOpenChange,
  done,
  bodyWithResult = "collapsed",
  children,
}: React.PropsWithChildren<{
  chatAction?: React.ReactNode;
  status?: React.ReactNode;
  repeatLabel?: string;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  result?: React.ReactNode;
  label: React.ReactNode;
  description?: string;
  optional?: boolean;
  stepNumber: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  done: boolean;
  /** With a result showing, the body sits behind `repeatLabel`, or is hidden when the result carries its own next action. */
  bodyWithResult?: "collapsed" | "hidden";
}>) {
  const body = (() => {
    if (!result) {
      return children;
    }
    if (bodyWithResult === "hidden") {
      return null;
    }
    if (!repeatLabel) {
      return children;
    }
    return (
      <CollapsibleRoot className="flex flex-col items-start gap-3">
        <CollapsibleTrigger className="max-w-full rounded-md px-3 py-2 text-sm font-medium hover:bg-sheet focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">
          {repeatLabel}
        </CollapsibleTrigger>
        <CollapsibleContent className="w-full">{children}</CollapsibleContent>
      </CollapsibleRoot>
    );
  })();
  return (
    <CollapsibleRoot
      open={open}
      onOpenChange={onOpenChange}
      className={cn(
        "group/step min-w-0 [overflow-wrap:anywhere] border-t-0.5 border-border first:border-t-0",
        className,
        UNSAFE_className,
      )}
    >
      <h4 className="min-w-0">
        <CollapsibleTrigger className="flex min-h-11 w-full items-center gap-3 rounded-md py-3 text-left text-sm font-medium text-default hover:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">
          <span className="flex w-8 shrink-0 justify-center [@media(pointer:coarse)]:w-11">
            <span
              className={cn(
                GLASS_PANEL_CLASS,
                "flex size-8 shrink-0 items-center justify-center rounded-full font-sans text-sm leading-none tabular-nums motion-reduce:transition-none [@media(pointer:coarse)]:size-11",
                done
                  ? "border-green-border bg-green-soft text-green"
                  : "text-muted group-data-[open]/step:text-default",
              )}
            >
              {done ? (
                <Check className="size-4" strokeWidth={2.5} aria-hidden />
              ) : (
                stepNumber
              )}
            </span>
          </span>
          <span className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <span className="inline-flex min-w-0 flex-wrap items-center gap-2">
              <span className="min-w-0 [overflow-wrap:anywhere]">{label}</span>
              {optional && (
                <span className="text-sm font-normal text-muted">Optional</span>
              )}
            </span>
          </span>
          {done && <span className="sr-only">Complete</span>}
          <ChevronDown
            aria-hidden
            className="size-3.5 shrink-0 text-muted transition-transform duration-fast group-data-[open]/step:rotate-180 motion-reduce:transition-none"
          />
        </CollapsibleTrigger>
      </h4>
      <CollapsibleContent>
        <div className="flex min-w-0 flex-col gap-3 pb-4 pl-11 [@media(pointer:coarse)]:pl-14">
          {description && (
            <p className="text-sm text-muted [overflow-wrap:anywhere]">
              {description}
            </p>
          )}
          {result}
          {chatAction}
          {status}
          {body}
        </div>
      </CollapsibleContent>
    </CollapsibleRoot>
  );
}
